/**
 * ScorecardDashboard — the MTD and DPD tabs.
 *
 * ONE component, two modes. `<ScorecardDashboard />` is MTD;
 * `<ScorecardDashboard dpd />` is the days-past-due breakdown.
 *
 * The two differ in three things — a band filter, a chart type, and which rate
 * they show — and agree on everything else: the period definitions, the
 * funnel, the table, the pooled baselines, the zone handling, the roll-up
 * rules. Split across two files those would be two copies to keep in step, and
 * the first thing to drift would be a baseline definition, silently.
 *
 * ── SAME DAY AND KEPT ──
 * The money that comes in is now two nested figures: Same Day (paid_amount, the
 * immediate payments) and Kept (paid_amount + approved_amount, same day plus
 * the promise payments that later cleared, from PtpPaymentStatus). PTP ⊇ Kept ⊇
 * Same Day, so the chart nests them as three green shades and the funnel steps
 * down through them. On the DPD tab approved has no dpd to band, so Kept
 * collapses to Same Day there until it can be bucketed server-side.
 *
 * ── FOUR GRANULARITIES, IN TWO PAIRS ──
 * Month and Week are spans. Date and Day are those same spans narrowed to a
 * single day: Date is the month comparison at one day number — the 9th against
 * the last twelve 9ths — and Day is the week comparison at one weekday.
 *
 * Each of the two single-day granularities carries a picker for WHICH day, and
 * the two pickers differ in shape for one reason only: seven weekdays fit as
 * pills, and thirty-one dates do not.
 *
 * ── THE SERVER DOES THE WORK ──
 * This receives finished rows and formats them. The period arithmetic, the
 * zone conversion and the rates all happen in the same query rpt1 runs, so the
 * tabs and the emailed report cannot disagree.
 *
 * ── ONE REQUEST PER GRANULARITY ──
 * The fetch sends `grp`, so a Month view builds its months plus the two
 * context rows rather than all fifty periods. The period join multiplies rows
 * against the hourly table, so the groups not on screen were most of the
 * query's work: 34 periods produced 630,906 rows against roughly 186,000 for a
 * month view.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem, Select, Typography,
} from "@mui/material";
import {
  Area, Bar, CartesianGrid, ComposedChart, LabelList, Legend,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { getDashboardScorecard } from "../../services/pbi/pbiservices";
import { useClientTimeZone, zoneAbbr, zonedNow } from "../../utils/clienttimezone";

// ── Palette ────────────────────────────────────────────────────────
// Matches the report's chart colours so a reader moving between the PDF and
// this tab sees the same series in the same hue.
const BLUE      = "#378ADD";
const GREEN     = "#1D9E75";
const AMBER     = "#BA7517";   // funnel: PTP step
const BAND      = "#1D9E75";   // baseline band — a hue no series uses
const BAND_INK  = "#0F6E56";

// One colour per PERIOD — these are the bars inside each DPD band.
//
// ⚠ ORDERED BY HUE, NOT BY SHADE. An earlier version ran light-to-dark blue
// and was built when these were bands, where a gradient reads as an ordered
// scale. As periods they are categories, and five shades of the same blue are
// only separable side by side — which is exactly the comparison a reader is
// trying to make across bands, several inches apart.
//
// So the first entries are maximally distinct: blue, amber, teal, pink,
// purple. Shade variants only appear from index 5, by which point the chart
// has more series than anyone reads precisely anyway, and the bar labels carry
// the period name regardless.
const SERIES_COLORS = [
  "#378ADD",  // blue
  "#BA7517",  // amber
  "#1D9E75",  // teal
  "#D4537E",  // pink
  "#7F77DD",  // purple
  "#639922",  // olive
  "#2C6FB5",  // deep blue
  "#A3611A",  // deep amber
  "#0F6E56",  // deep teal
  "#B03A63",  // deep pink
  "#6A5FD0",  // deep purple
];
const seriesColor = (i) => SERIES_COLORS[i % SERIES_COLORS.length];

// Darken (pct < 0) or lighten (pct > 0) a hex colour.
//
// Used for the nested Kept and Same Day segments inside a PTP bar, and for the
// funnel's Kept and Same Day steps. Each is a PART of PTP, not a sibling of it,
// so they must read as one quantity split rather than separate quantities side
// by side — which different hues would say. Darker shades of the same hue are
// the one case where shading is the right encoding: here it IS an ordered
// relationship, the darker part contained in the lighter whole.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

// ── THREE GREENS, NESTED — matching rpt1 ──
//   PTP       mid green    the whole (outer)
//   Kept      -0.21        same day + approved = total collected (middle band)
//   Same Day  -0.42        immediate payments only (innermost band)
// Kept ⊇ Same Day ⊇ 0, and PTP ⊇ Kept, so one hue at three values says the
// containment the numbers already have.
const KEPT_SHADE    = -0.21;   // middle band
const SAMEDAY_SHADE = -0.42;   // innermost band

// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward, so it is the one that wins: RPC
// blue, PTP green, then two darker shades of that green for Kept and Same Day.
// Accounts takes brown, which is what freed blue for RPC.
//
// A metric reads the same hue wherever it appears — funnel bar, KPI figure,
// line, area, table column — on every page and in the emailed PDF. Colour
// identifies rather than decorates.
//
// ⚠ NAMED CONSTANTS RATHER THAN THE BARE BLUE/GREEN/AMBER THIS FILE USED.
// The funnel steps and the money pill both referenced the raw hues, so
// changing what colour PTP is meant editing four call sites and hoping none
// were missed. Now the metric names it and the hue follows.
const C_ACCOUNTS = "#8A5524";                    // brown
const C_RPC      = BLUE;                          // #378ADD, RPC %
const C_PTP      = GREEN;                          // #1D9E75, PTP $ (outer)
const C_KEPT     = shade(C_PTP, KEPT_SHADE);       // mid green — total collected
const C_SAMEDAY  = shade(C_PTP, SAMEDAY_SHADE);    // dark green — same-day only

// ── Formatting ─────────────────────────────────────────────────────
const num = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };

const fmtInt   = (v) => num(v).toLocaleString();
const fmtPct   = (v) => `${num(v).toFixed(2)}%`;
// 14,842,998.70 -> "14,843K". The magnitude is the point; the cents are noise
// and the full figure is the widest thing on the row.
const fmtMoneyK = (v) => `${Math.round(num(v) / 1000).toLocaleString()}K`;

const DOW_LABELS  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DOW_FULL    = ["Sunday", "Monday", "Tuesday", "Wednesday",
                     "Thursday", "Friday", "Saturday"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── THE DAY RANGE A GROUP COVERS ──
//
// "Kept $ by month" is true but incomplete: every month row runs from the 1st
// to the ANCHOR's day number, not to month end. A reader who does not know
// that reads the chart as whole months and the numbers as low.
//
// So the title carries the span — "Days 1-7", "Mon-Fri", "Fridays", "Day 9" —
// taken from the CURRENT period, which every other period in the group is
// matched to. It is the same disclosure the funnel title makes with its date
// range, in the form that suits a whole series rather than one period.
const periodQualifier = (grp, row) => {
    if (!row || !row.range_start || !row.range_end) return "";
    const a = new Date(row.range_start), b = new Date(row.range_end);
    if (isNaN(a) || isNaN(b)) return "";
    if (grp === "month") return `Days ${a.getUTCDate()}-${b.getUTCDate()}`;
    if (grp === "week")  return `Mon-${DOW_LABELS[b.getUTCDay()]}`;
    // A day group is twelve occurrences of one weekday, so the plural names
    // the whole series rather than any one of them. Full name, not the
    // abbreviation: "Fris" is not a word.
    if (grp === "day")   return `${DOW_FULL[b.getUTCDay()]}s`;
    // A date group is one day number across twelve months. ⚠ TRUE OF THE TOP
    // ROW ONLY: a 31st clamps to the 30th in April and the 28th in February,
    // so the series is not all one number. The Dates column carries the rest,
    // and the dropdown says which date was asked for.
    if (grp === "date")  return `Day ${b.getUTCDate()}`;
    return "";
};

const shortDateRange = (row, anchorYear) => {
  const one = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d)) return "";
    const base = `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
    return d.getUTCFullYear() === anchorYear
      ? base
      : `${base}/${String(d.getUTCFullYear()).slice(2)}`;
  };
  // A date, day or prior row is ONE day, so the general form prints the same
  // string twice with a dash in it. Collapse it — the column is narrow and the
  // repetition says nothing.
  const s = one(row.range_start), e = one(row.range_end);
  if (!s || !e) return "";
  return s === e ? s : `${s}-${e}`;
};

// ── Baselines ──────────────────────────────────────────────────────
//
// ── POOLED, NOT AVERAGED ──
// For a RATE the baseline is total numerator over total denominator across
// the window, not the mean of the periods' percentages. Averaging percentages
// weights a 900-account month the same as a 100-account one; on a book whose
// volume ramped, the two answers differ by multiples, and the mean is the
// wrong one.
//
// It matters most on the two single-day granularities, where a quiet date with
// nine accounts would otherwise carry the same weight as a busy one with nine
// hundred and the line would sit wherever the quiet days put it.
const pooledPct = (rows, numKey, denKey) => {
  const den = rows.reduce((a, r) => a + num(r[denKey]), 0);
  return den ? (rows.reduce((a, r) => a + num(r[numKey]), 0) * 100) / den : 0;
};

// For a TOTAL there is no ratio to pool, so it is the mean — over periods
// that had activity only. A period the system was not running in is not a
// zero, and counting it as one drags the line down and makes every real
// period look above trend.
const activeMean = (rows, key) => {
  const active = rows.filter((r) => num(r.accounts_worked) > 0);
  return active.length
    ? active.reduce((a, r) => a + num(r[key]), 0) / active.length
    : 0;
};

// ── Chart ──────────────────────────────────────────────────────────
//
// Area, matching the Trends dashboard rather than the report's bars. A period
// series is a shape over time, and the filled curve says that; twelve bars say
// twelve separate things that happen to be adjacent.
//
// ── THE AVERAGE IS A DOTTED RULE, NOTHING MORE ──
// An earlier version shaded everything below the average. Under bars that
// worked; under a filled area it is a fill inside a fill, and the eye reads
// the overlap as a third value. The line alone carries the level, and the
// label in the header carries the number.
//
// ── NESTED PARTS ──
// `parts` is the inner bands, outermost-first: PTP carries [Kept, Same Day],
// Kept carries [Same Day], Same Day carries none. Each is a PART of the total,
// drawn as a stacked segment from the axis up to its own curve — never summed,
// since ptp_balance ≥ kept_amount ≥ paid_amount already. A band whose value
// equals the one inside it (approved = 0) draws at zero height and its label is
// suppressed, so a period with no cleared promises simply shows Same Day and
// PTP. Colours are darker inward; the topmost band (the total) keeps the
// gradient fill.
function ScoreChart({ id, title, rows, field, parts = [], label, color, unit, baseline }) {
  const inner = parts || [];
  const n = inner.length;
  const stacked = n > 0;

  const data = useMemo(() => rows.map((r) => {
    const total = num(r[field]);
    // Nested cumulative values, clamped so total ≥ inner[0] ≥ inner[1] … ≥ 0.
    const cum = [];
    inner.forEach((p, j) => {
      let v = Math.min(Math.max(0, num(r[p.field])), total);
      if (j > 0) v = Math.min(v, cum[j - 1]);
      cum.push(v);
    });
    const d = { name: r.period_short || r.period, full: r.period, value: total };
    if (n > 0) {
      // Segments bottom→top: s_0 innermost … s_n top. c_i is the cumulative
      // upper edge each layer sits at, used for its label.
      d.s_0 = cum[n - 1];
      d.c_0 = cum[n - 1];
      for (let i = 1; i < n; i++) {
        d[`s_${i}`] = cum[n - 1 - i] - cum[n - i];
        d[`c_${i}`] = cum[n - 1 - i];
      }
      d[`s_${n}`] = Math.max(0, total - cum[0]);
      d[`c_${n}`] = total;
    }
    return d;
  }), [rows, field, inner]);

  const fmtVal = unit === "%" ? fmtPct : fmtMoneyK;
  // Gradient ids are document-global in SVG, so two charts sharing one would
  // both take whichever rendered last. `id` is per chart for that reason —
  // and the money chart folds its metric into the id, because switching the
  // pill swaps the colour and a shared gradient would keep the old one.
  const gradId = `grad_${id}`;

  // Layer i (0 = innermost/bottom … n = total/top): colour + legend name.
  const layers = [
    ...inner.slice().reverse().map((p) => ({ color: p.color, name: p.label, top: false })),
    { color, name: label, top: true },
  ];

  if (!data.length) {
    return (
      <ChartCard title={title}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center",
                   height: "100%", fontSize: 12, color: "text.disabled" }}>
          No data for this period
        </Box>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} right={`avg ${fmtVal(baseline)}`}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.15} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#888" }} />
          <YAxis tick={{ fontSize: 10, fill: "#888" }} tickFormatter={fmtVal} />
          <Tooltip
            contentStyle={{ fontSize: 12 }}
            formatter={(v) => [fmtVal(v), label]}
            labelFormatter={(_, p) => p?.[0]?.payload?.full || ""}
          />
          <ReferenceLine y={baseline} stroke={BAND_INK}
                         strokeDasharray="3 3" strokeWidth={1} />

          {stacked ? (
            layers.map((L, i) => (
              <Area key={i} type="monotone" dataKey={`s_${i}`} name={L.name} stackId="m"
                    stroke={L.color}
                    fill={L.top ? `url(#${gradId})` : L.color}
                    fillOpacity={L.top ? 1 : (i === 0 ? 0.72 : 0.5)}
                    strokeWidth={2} dot={{ r: 2.2 }}>
                <LabelList dataKey={`c_${i}`}
                           position={L.top ? "top" : "insideTop"}
                           offset={L.top ? 8 : 6}
                           fontSize={L.top ? 9 : 8}
                           fill={L.top ? L.color : "#fff"}
                           formatter={(v) => (num(v) > 0 ? fmtVal(v) : "")} />
              </Area>
            ))
          ) : (
            <Area type="monotone" dataKey="value" name={label}
                  stroke={color} fill={`url(#${gradId})`} strokeWidth={2}
                  dot={{ r: 3 }}>
              <LabelList dataKey="value" position="top" offset={8}
                         fontSize={9} fill={color} formatter={fmtVal} />
            </Area>
          )}
          {stacked && <Legend wrapperStyle={{ fontSize: 11 }} />}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ── Funnel ─────────────────────────────────────────────────────────
//
// Current period only. A funnel is a snapshot of one period's flow — stacking
// twelve of them, or averaging them, answers nothing — so this reads the top
// row and the other eleven periods stay in the charts and the table where a
// comparison belongs.
//
// ── THE BAR'S LENGTH IS THE NUMBER PRINTED IN IT ──
// The fill is the STEP-TO-STEP rate — PTPs over RPCs, kept $ over promised $
// — which is the figure shown inside the bar. A bar filled to 11% reads
// "10.66%", and the two agree.
//
// ⚠ ON THE DATE AND DAY GRANULARITIES THIS DESCRIBES ONE DAY. Every rate here
// then sits on a single day's denominator, and Kept % in particular can move
// tens of points on one payment landing. The charts behind it are what say
// whether the day was normal.
//
// ── SAME DAY AND KEPT ──
// The money that arrives is two steps now: Kept (same day + approved, of
// promised $) then Same Day (immediate only, of promised $), each a darker
// shade of PTP. Kept is only drawn when approved actually adds to same-day —
// so on the DPD tab, where approved is not available per band, the step
// collapses out and only Same Day shows.
//
// RPCs carries no small line. Its step rate IS its share of accounts — the
// step above it is accounts — so the two lines would print the same number
// twice. Kept carries no count line (total collected has no single count);
// it shows the approved dollars it adds instead.
const FUNNEL_STEPS = (row) => {
  const accounts = num(row.accounts_worked);
  const shareOf = (v) => (accounts > 0 ? (num(v) / accounts) * 100 : 0);
  const ofAccounts = (v) => `${shareOf(v).toFixed(1)}% of accnts`;

  const paid = num(row.paid_amount);
  const kept = num(row.kept_amount);
  const approved = num(row.approved_amount);

  return [
      {
          label: "Accounts", color: C_ACCOUNTS,
          count: accounts, money: num(row.accounts_balance),
          share: 100, fill: 100, inside: fmtInt(accounts), secondary: null,
      },
      {
          label: "RPCs", color: C_RPC,
          count: num(row.rpcs), money: null,
          share: shareOf(row.rpcs),
          fill: num(row.rpc_pct),
          inside: fmtPct(row.rpc_pct),
          secondary: null,
      },
      {
          label: "PTPs", color: C_PTP,
          count: num(row.ptps), money: num(row.ptp_balance),
          share: shareOf(row.ptps),
          fill: num(row.ptp_pct),
          inside: fmtPct(row.ptp_pct),
          secondary: ofAccounts(row.ptps),
      },
      {
          // ── Kept — total collected (same day + approved), of promised $ ──
          // Always shown, ABOVE Same Day. Middle green. When no promises have
          // cleared yet, kept == same day and the "approved" note is omitted;
          // once approved money exists it appears and the two steps diverge.
          label: "Kept", color: C_KEPT,
          count: null, money: kept,
          share: null,
          fill: num(row.kept_pct_total),
          inside: fmtPct(row.kept_pct_total),
          secondary: approved > 0 ? `incl. ${fmtMoneyK(approved)} approved` : null,
      },
      {
          // ── Same Day — immediate payments only (was "Kept") ── darkest green.
          label: "Same Day", color: C_SAMEDAY,
          count: num(row.kept_count), money: paid,
          share: shareOf(row.kept_count),
          fill: num(row.kept_pct),
          inside: fmtPct(row.kept_pct),
          secondary: ofAccounts(row.kept_count),
      },
  ];
};

function Funnel({ row }) {
    if (!row) {
        return (
            <Box sx={{ fontSize: 12, color: "text.disabled", p: 1 }}>
                No data for this period
            </Box>
        );
    }

    const steps = FUNNEL_STEPS(row);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5 }}>
            {steps.map((s, i) => {
                const isFirst = i === 0;
                // Clamped at 100 for the case where a step out-converts its own
                // denominator.
                const fill = Math.min(100, Math.max(0, s.fill));
                return (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ position: "relative", height: 46,
                                       background: "#f0f0f0", borderRadius: "4px" }}>
                                <Box sx={{
                                    position: "absolute", left: 0, top: 0, bottom: 0,
                                    width: `${fill}%`, background: s.color,
                                    borderRadius: isFirst ? "4px" : "4px 0 0 4px",
                                }} />
                                <Box sx={{
                                    position: "absolute", inset: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 12, fontWeight: 600,
                                    color: isFirst ? "#fff" : "#333",
                                    pointerEvents: "none", whiteSpace: "nowrap",
                                }}>
                                    {s.inside}
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ width: 108, flexShrink: 0, fontSize: 12,
                                   color: "text.secondary", lineHeight: 1.35 }}>
                            <Box sx={{ color: s.color, fontWeight: 600 }}>{s.label}</Box>
                            <Box sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                                {s.count != null && fmtInt(s.count)}
                                {s.money != null &&
                                  `${s.count != null ? " · " : ""}${fmtMoneyK(s.money)}`}
                            </Box>
                            {s.secondary && (
                                <Box sx={{ mt: 0.25, fontSize: 10, color: "text.disabled",
                                           whiteSpace: "nowrap" }}>
                                    {s.secondary}
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

// ── Horizontal bars ────────────────────────────────────────────────
//
// Used by the DPD tab. The period labels run down the left instead of along
// the bottom, which is what makes a twelve-period axis readable once the tab
// also carries eleven bucket pills — a vertical axis has as much room as it
// needs, where the horizontal one was already tight at twelve.
//
// Same three layers as the area charts: the series, its value labels, and the
// dotted pooled average. The inner segment here is Same Day (paid_amount);
// approved has no dpd to band, so the DPD grouped bars nest Same Day only, not
// Kept.
function BandBarChart({ id, title, rows, buckets, field, partField, partLabel,
                       label, unit, baseline }) {
  const fmtVal  = unit === "%" ? fmtPct : fmtMoneyK;
  const stacked = Boolean(partField);

  // Periods, NEWEST FIRST — the current one is the top bar in every band,
  // where the eye lands, and older ones read downward as history.
  const periods = useMemo(() => {
    const firstSort = new Map();
    rows.forEach((r) => {
      const k = r.period_short || r.period;
      const v = num(r.sort);
      if (!firstSort.has(k) || v < firstSort.get(k)) firstSort.set(k, v);
    });
    return [...firstSort.entries()].sort((a, b) => a[1] - b[1]).map(([k]) => k);
  }, [rows]);

  // One entry per band, a column per period. Bands with nothing in them are
  // KEPT: the pill selection is a question, and a band that answers "none" is
  // an answer.
  const data = useMemo(() => {
    const byBucket = new Map(buckets.map((b) => [b, { name: b }]));
    rows.forEach((r) => {
      if (!r.bucket || !byBucket.has(r.bucket)) return;
      const key  = r.period_short || r.period;
      const cell = byBucket.get(r.bucket);
      if (stacked) {
        const total = num(r[field]);
        const part  = num(r[partField]);
        cell[`${key}__part`]  = part;
        cell[`${key}__rest`]  = Math.max(0, total - part);
        cell[`${key}__total`] = total;
      } else {
        cell[key] = num(r[field]);
      }
    });
    return [...byBucket.values()];
  }, [rows, buckets, field, partField, stacked]);

  if (!data.length || !periods.length) {
    return (
      <ChartCard title={title}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center",
                   height: "100%", fontSize: 12, color: "text.disabled" }}>
          No data for this period
        </Box>
      </ChartCard>
    );
  }

  const height = Math.max(300, data.length * (periods.length * 14 + 22) + 80);

  return (
    <ChartCard title={title} right={`avg ${fmtVal(baseline)}`} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} layout="vertical"
                       margin={{ top: 4, right: 92, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: "#888" }} tickFormatter={fmtVal} />
          <YAxis type="category" dataKey="name" width={52}
                 tick={{ fontSize: 11, fill: "#666" }} />
          <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, n) => [fmtVal(v), n]} />
          <ReferenceLine x={baseline} stroke={BAND_INK}
                         strokeDasharray="3 3" strokeWidth={1} />
          {periods.map((per, i) => {
            const c = seriesColor(i);
            const outerLabel = (
              <LabelList dataKey={stacked ? `${per}__total` : per}
                         position="right" offset={4} fontSize={8} fill={c}
                         formatter={(v) => (v == null ? "" : `${per} · ${fmtVal(v)}`)} />
            );

            if (!stacked) {
              return (
                <Bar key={per} dataKey={per} name={per} fill={c}
                     radius={[0, 2, 2, 0]} barSize={10}>
                  {outerLabel}
                </Bar>
              );
            }

            return (
              <React.Fragment key={per}>
                {/* Same Day, at the axis end, darker. It carries the legend
                    entry; the remainder is legendType="none" so one period does
                    not appear in the legend twice. */}
                <Bar dataKey={`${per}__part`} name={per} stackId={per}
                     fill={shade(c, SAMEDAY_SHADE)} barSize={10}>
                  <LabelList dataKey={`${per}__part`} position="insideRight"
                             offset={4} fontSize={8} fill="#fff"
                             formatter={(v) => (num(v) > 0 ? fmtVal(v) : "")} />
                </Bar>
                <Bar dataKey={`${per}__rest`} name={`${per} unpaid`} stackId={per}
                     fill={c} fillOpacity={0.55} radius={[0, 2, 2, 0]}
                     barSize={10} legendType="none">
                  {outerLabel}
                </Bar>
              </React.Fragment>
            );
          })}
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function ChartCard({ title, right, children, height = 240 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
                          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {title}
        </Typography>
        {right && (
          <Typography sx={{ fontSize: 11, color: BAND_INK }}>{right}</Typography>
        )}
      </Box>
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>
        {children}
      </Box>
    </Box>
  );
}

// ── Table ──────────────────────────────────────────────────────────
const COLUMNS = [
  { key: "period",           label: "Period",     align: "left" },
  { key: "date_range",       label: "Dates",      align: "left" },
  { key: "accounts_worked",  label: "Accounts",   fmt: fmtInt },
  { key: "accounts_balance", label: "Accounts $", fmt: fmtMoneyK },
  { key: "rpcs",             label: "RPCs",       fmt: fmtInt },
  { key: "ptps",             label: "PTPs",       fmt: fmtInt },
  { key: "ptp_balance",      label: "PTP $",      fmt: fmtMoneyK },
  { key: "paid_amount",      label: "Same Day $", fmt: fmtMoneyK }, // was "Kept $"
  { key: "kept_amount",      label: "Kept $",     fmt: fmtMoneyK }, // new: paid + approved
  { key: "rpc_pct",          label: "RPC %",      fmt: fmtPct },
  { key: "ptp_pct",          label: "PTP %",      fmt: fmtPct },
  { key: "kept_pct",         label: "Same Day %", fmt: fmtPct },    // was "Kept %"
  { key: "kept_pct_total",   label: "Kept %",     fmt: fmtPct },    // new
];

// `groups` renders a banded table: a DPD band header, then that band's
// periods beneath it. `rows` renders a flat one. Both share the column set,
// so the two modes cannot drift on formatting.
function ScoreTable({ rows, groups }) {
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={{
        width: "100%", borderCollapse: "collapse", fontSize: 12,
        "& th, & td": { padding: "7px 10px", whiteSpace: "nowrap" },
        "& th": {
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.04em",
          background: "rgba(0,0,0,0.02)",
          borderBottom: "0.5px solid", borderColor: "divider",
        },
        ...(groups ? {} : { "& tbody tr:nth-of-type(even)": { background: "rgba(0,0,0,0.015)" } }),
        ...(groups ? { "& tbody tr td[colspan]": { paddingTop: "14px", background: "rgba(0,0,0,0.02)" } } : {}),
      }}>
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.key} style={{ textAlign: c.align || "right" }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups
            ? groups.map((g) => [
                <tr key={`h-${g.label}`}>
                  <td colSpan={COLUMNS.length} style={{ textAlign: "left" }}>
                    <Box component="span" sx={{
                      fontSize: 11, fontWeight: 600, color: BLUE,
                      textTransform: "uppercase", letterSpacing: "0.05em",
                    }}>
                      {g.label} days past due
                    </Box>
                  </td>
                </tr>,
                ...(g.rows.length
                  ? g.rows.map((r) => (
                      <tr key={`${g.label}-${r.sort}`}>
                        {COLUMNS.map((c) => (
                          <td key={c.key} style={{ textAlign: c.align || "right" }}>
                            {c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                          </td>
                        ))}
                      </tr>
                    ))
                  : [
                      <tr key={`${g.label}-empty`}>
                        <td colSpan={COLUMNS.length}
                            style={{ textAlign: "left", color: "#999" }}>
                          No accounts in this band
                        </td>
                      </tr>,
                    ]),
              ])
            : rows.map((r) => (
                <tr key={r.sort}>
                  {COLUMNS.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align || "right" }}>
                      {c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </Box>
    </Box>
  );
}

// Multi-select pills. `All` is exclusive: picking it clears the rest, and
// picking anything else clears All — the two are different queries, not a
// selection that happens to be complete.
function MultiPillGroup({ label, allLabel, options, selected, onChange }) {
  const allActive = selected.length === 0;
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((x) => x !== opt));
    else onChange([...selected, opt]);
  };
  const pill = (text, active, onClick) => (
    <Box
      key={text}
      onClick={onClick}
      sx={{
        px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
        cursor: "pointer", userSelect: "none",
        border: "0.5px solid",
        borderColor: active ? BLUE : "divider",
        background:  active ? BLUE : "transparent",
        color:       active ? "#fff" : "text.secondary",
        fontWeight:  active ? 500 : 400,
        transition:  "all .15s",
        "&:hover": { borderColor: BLUE },
      }}
    >
      {text}
    </Box>
  );
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
                        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {pill(allLabel, allActive, () => onChange([]))}
        {options.map((o) => pill(o.label, selected.includes(o.label), () => toggle(o.label)))}
      </Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
                        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <Box
              key={opt}
              onClick={() => onChange(opt)}
              sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none",
                border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition:  "all .15s",
                "&:hover": { borderColor: BLUE },
              }}
            >
              {opt}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── A DROPDOWN, NOT PILLS ──
// The weekday picker is seven pills and fits on the filter row. The date
// picker is up to thirty-one, which would wrap and push the charts below the
// fold — and unlike the DPD bands, this is a single choice made once.
function SelectGroup({ label, options, value, onChange, minWidth = 108 }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
                        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Select
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
        size="small"
        displayEmpty
        sx={{
          minWidth, height: 26, fontSize: 12, borderRadius: "20px",
          color: "text.secondary",
          "& .MuiSelect-select": { py: "3px", pl: "12px" },
          "& .MuiOutlinedInput-notchedOutline": { borderWidth: "0.5px" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "0.5px", borderColor: BLUE,
          },
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12 }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

// ── Granularity ────────────────────────────────────────────────────
const GRAN_OPTIONS = ["Day", "Date", "Week", "Month"];

// ── THE MONEY CHART IS A PILL ──
// PTP $ is the default: stacked, it answers everything at once — the bar's
// length is what was promised, the middle band is what was ultimately Kept
// (same day + approved), and the darkest base is what came in Same Day. Kept $
// and Same Day $ pull each figure out on its own.
//
// `parts` drives the area chart (MTD), outermost-first. `partField`/`partLabel`
// drive the DPD grouped bars, which can only nest Same Day (approved has no dpd
// to band). Colours match the funnel steps.
const MONEY_METRICS = {
  "Same Day $": { field: "paid_amount", color: C_SAMEDAY, parts: [] },
  "Kept $":     { field: "kept_amount", color: C_KEPT,
                  parts: [{ field: "paid_amount", label: "Same Day $", color: C_SAMEDAY }],
                  partField: "paid_amount", partLabel: "Same Day $" },
  "PTP $":      { field: "ptp_balance", color: C_PTP,
                  parts: [
                    { field: "kept_amount", label: "Kept $",     color: C_KEPT },
                    { field: "paid_amount", label: "Same Day $", color: C_SAMEDAY },
                  ],
                  partField: "paid_amount", partLabel: "Same Day $" },
};
const MONEY_OPTIONS = Object.keys(MONEY_METRICS);

// Weekday pills, shown only at Day granularity. Sun..Sat so the index IS the
// value the API wants. Displayed Mon-first because the week rows are
// Mon-anchored.
const DOW_DISPLAY = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ── DPD BUCKETS ──
const DPD_ALL = "All";
const DPD_BUCKETS = [
  { label: "1-5",   lo: 1,  hi: 5    },
  { label: "6-10",  lo: 6,  hi: 10   },
  { label: "11-15", lo: 11, hi: 15   },
  { label: "16-20", lo: 16, hi: 20   },
  { label: "21-25", lo: 21, hi: 25   },
  { label: "26-30", lo: 26, hi: 30   },
  { label: "31-35", lo: 31, hi: 35   },
  { label: "36-40", lo: 36, hi: 40   },
  { label: "41-50", lo: 41, hi: 50   },
  { label: "51-60", lo: 51, hi: 60   },
  { label: "61+",   lo: 61, hi: 9999 },
];

const dpdParam = (selected) => {
  const chosen = selected.length
    ? DPD_BUCKETS.filter((b) => selected.includes(b.label))
    : DPD_BUCKETS;
  return chosen.map((b) => `${b.lo}-${b.hi}`).join(",");
};

// ── THE ANCHOR, RESOLVED CLIENT-SIDE ── Yesterday in the tenant's zone.
function anchorDate(timeZone) {
  const d = zonedNow(timeZone);
  d.setDate(d.getDate() - 1);
  return d;
}

// Days 1..anchor of the anchor's month, NEWEST FIRST.
const dateOptions = (anchor) => {
  const m = anchor.getMonth(), max = anchor.getDate();
  const out = [];
  for (let d = max; d >= 1; d--) out.push({ value: d, label: `${MONTH_SHORT[m]} ${d}` });
  return out;
};

const GROUPS = {
  Month: {
    grp: "month",
    title: "Month comparison",
    rate: {
      id: "rate_month", field: "rpc_pct", label: "RPC %", color: C_RPC,
      title: "RPC % by month",
      baseline: (rows) => pooledPct(rows, "rpcs", "accounts_worked"),
    },
    money: { id: "money_month", by: "month" },
    funnelName: (row) => row.period_short || row.period,
    funnelFallback: "month to date",
  },
  Week: {
    grp: "week",
    title: "Week comparison",
    rate: {
      // kept_pct is same-day $ over promised $, so this is Same Day % now.
      id: "rate_week", field: "kept_pct", label: "Same Day %", color: C_SAMEDAY,
      title: "Same Day % by week",
      baseline: (rows) => pooledPct(rows, "paid_amount", "ptp_balance"),
    },
    money: { id: "money_week", by: "week" },
    funnelName: (row) => `Wk ${row.period_short ?? ""}`.trim(),
    funnelFallback: "current week",
  },
  Date: {
    grp: "date",
    title: "Date comparison",
    rate: {
      id: "rate_date", field: "rpc_pct", label: "RPC %", color: C_RPC,
      title: "RPC % by date",
      baseline: (rows) => pooledPct(rows, "rpcs", "accounts_worked"),
    },
    money: { id: "money_date", by: "date" },
    funnelName: (row) => row.period_short || row.period,
    funnelFallback: "most recent date",
  },
  Day: {
    grp: "day",
    title: "Day comparison",
    rate: {
      id: "rate_day", field: "rpc_pct", label: "RPC %", color: C_RPC,
      title: "RPC % by day",
      baseline: (rows) => pooledPct(rows, "rpcs", "accounts_worked"),
    },
    money: { id: "money_day", by: "day" },
    funnelName: (row) => {
      const d = row.range_start ? new Date(row.range_start) : null;
      return d && !isNaN(d) ? DOW_LABELS[d.getUTCDay()] : (row.period_short || row.period);
    },
    funnelFallback: "most recent",
  },
};

const CONTEXT_GRPS = ["prior", "year"];

const ALL_GRPS = ["prior", "year", "month", "date", "week", "day"];

// ── The data hook ──────────────────────────────────────────────────
function useScorecard({ gran, dpd = false, buckets = [] }) {
  const [rows,       setRows]       = useState([]);
  const [funnelRows, setFunnelRows] = useState([]);   // DPD tab: unbanded, for the funnel
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [dow,        setDow]        = useState(null);
  const [dom,        setDom]        = useState(null);

  const { timeZone, tzReady } = useClientTimeZone();

  const anchor = useMemo(
    () => (tzReady ? anchorDate(timeZone) : null),
    [tzReady, timeZone]
  );

  useEffect(() => {
    if (!anchor) return;
    if (dow == null) setDow(anchor.getDay());
    if (dom == null) setDom(anchor.getDate());
  }, [anchor, dow, dom]);

  useEffect(() => {
    if (!tzReady) return;
    let cancelled = false;
    setLoading(true);
    setError("");

    const params = { grp: GROUPS[gran].grp };
    if (gran === "Day"  && dow != null) params.dow = dow;
    if (gran === "Date" && dom != null) params.dom = dom;
    if (dpd) {
      const d = dpdParam(buckets);
      if (d) params.dpd = d;
    }

    getDashboardScorecard(params)
      .then((data) => { if (!cancelled) setRows(data || []); })
      .catch((e) => { if (!cancelled) { setError("Failed to load scorecard"); console.error(e); } })
      .finally(() => { if (!cancelled) setLoading(false); });

    // ── DPD FUNNEL MATCHES MTD ──
    // The funnel is a period snapshot that sums the bands back up, and approved
    // has no dpd to band — so on the DPD tab it can't be recovered from the
    // banded rows (approved is null per band, and the roll-up sums to zero,
    // collapsing Kept onto Same Day). We fetch the SAME period UNBANDED (no dpd
    // param, same grp/day) purely for the funnel's current row, whose approved
    // is the true period total. Result: the DPD funnel is identical to MTD's.
    // MTD needs no second call — the funnel already has the unbanded row.
    if (dpd) {
      const fparams = { grp: GROUPS[gran].grp };
      if (gran === "Day"  && dow != null) fparams.dow = dow;
      if (gran === "Date" && dom != null) fparams.dom = dom;
      getDashboardScorecard(fparams)
        .then((data) => { if (!cancelled) setFunnelRows(data || []); })
        .catch(() => { if (!cancelled) setFunnelRows([]); });
    } else if (funnelRows.length) {
      setFunnelRows([]);
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tzReady, gran, dow, dom, dpd, JSON.stringify(buckets)]);

  const prepared = useMemo(() => {
    const anchorYear = Math.max(
      0, ...rows.map((r) => (r.range_end ? new Date(r.range_end).getUTCFullYear() : 0))
    );

    // paid_amount = Same Day; approved_amount = promise $ that later cleared;
    // Kept = their sum. On the DPD tab approved comes back null per band, so
    // Kept collapses to Same Day there until approved can be banded.
    const derive = (r) => {
      const paid = num(r.paid_amount);
      const approved = num(r.approved_amount);
      const ptpBal = num(r.ptp_balance);
      const kept = paid + approved;
      return {
        ...r,
        approved_amount: approved,
        kept_amount: kept,
        kept_pct_total: ptpBal ? (kept * 100) / ptpBal : 0,
      };
    };

    const withDates = rows.map((r) =>
      derive({ ...r, date_range: shortDateRange(r, anchorYear) }));

    // In DPD mode the response is one row per (period, band). The funnel and
    // the table want one row per PERIOD, so the bands are added back up.
    // Summing is safe because an account holds one dpd per period. The RATES
    // are recomputed from the summed components rather than averaged.
    const rollUp = (list) => {
      const by = new Map();
      list.forEach((r) => {
        const k = r.sort;
        if (!by.has(k)) {
          by.set(k, { ...r, bucket: null, bucket_sort: null,
                      accounts_worked: 0, accounts_balance: 0, rpcs: 0,
                      ptps: 0, kept_count: 0, ptp_balance: 0, paid_amount: 0,
                      approved_amount: 0 });
        }
        const t = by.get(k);
        ["accounts_worked","accounts_balance","rpcs","ptps",
         "kept_count","ptp_balance","paid_amount","approved_amount"]
          .forEach((f) => { t[f] += num(r[f]); });
      });
      return [...by.values()].map((t) => ({
        ...t,
        kept_amount: t.paid_amount + t.approved_amount,
        rpc_pct:  t.accounts_worked ? (t.rpcs * 100) / t.accounts_worked : 0,
        ptp_pct:  t.rpcs            ? (t.ptps * 100) / t.rpcs            : 0,
        kept_pct: t.ptp_balance     ? (t.paid_amount * 100) / t.ptp_balance : 0,
        kept_pct_total: t.ptp_balance
          ? ((t.paid_amount + t.approved_amount) * 100) / t.ptp_balance : 0,
      }));
    };

    // Periods with no accounts worked are dropped from the series groups; the
    // two CONTEXT rows always show, even at zero.
    const pick = (grp) => {
      const g = withDates.filter((r) => r.grp === grp);
      const rolled = dpd ? rollUp(g) : g;
      return CONTEXT_GRPS.includes(grp)
        ? rolled
        : rolled.filter((r) => num(r.accounts_worked) > 0);
    };

    const out = { raw: {} };
    ALL_GRPS.forEach((g) => {
      out[g] = pick(g);
      out.raw[g] = withDates.filter((r) => r.grp === g);
    });
    return out;
  }, [rows, dpd]);

  // Current-period funnel row. MTD: null (the component uses its own current
  // row). DPD: derived from the UNBANDED funnelRows so Kept = Same Day +
  // approved and the whole funnel matches the MTD tab.
  const funnelRow = useMemo(() => {
    if (!dpd) return null;
    const grp = GROUPS[gran].grp;
    const g = funnelRows.filter((r) => r.grp === grp);
    if (!g.length) return null;
    const anchorYear = Math.max(
      0, ...funnelRows.map((r) => (r.range_end ? new Date(r.range_end).getUTCFullYear() : 0))
    );
    const r = g[0];
    const paid = num(r.paid_amount);
    const approved = num(r.approved_amount);
    const ptpBal = num(r.ptp_balance);
    return {
      ...r,
      date_range: shortDateRange(r, anchorYear),
      approved_amount: approved,
      kept_amount: paid + approved,
      kept_pct_total: ptpBal ? ((paid + approved) * 100) / ptpBal : 0,
    };
  }, [dpd, gran, funnelRows]);

  return { rows, prepared, funnelRow, loading, error, dow, setDow, dom, setDom,
           anchor, timeZone, tzReady };
}


// ── Main ───────────────────────────────────────────────────────────
export default function ScorecardDashboard({ dpd = false }) {
  const [gran,    setGran]    = useState("Month");
  const [buckets, setBuckets] = useState([]);   // [] means All — no filter
  // PTP $ on open: stacked, it carries all three figures at once — promised as
  // the length, Kept as the middle band, Same Day as the dark base.
  const [money,   setMoney]   = useState("PTP $");

  const { prepared, funnelRow, loading, error, dow, setDow, dom, setDom, anchor, timeZone } =
    useScorecard({ gran, dpd, buckets });

  const cfg      = GROUPS[gran];
  const moneyCfg = MONEY_METRICS[money];
  const list = prepared[cfg.grp] || [];

  const qualifier = periodQualifier(cfg.grp, list[0]);
  const titleOf = (t) => (qualifier ? `${t} · ${qualifier}` : t);

  const current = list[0] || null;
  // On the DPD tab the funnel uses the unbanded period row (so Kept includes
  // approved and matches MTD); on MTD it is just the current period. Falls back
  // to current while the unbanded fetch is in flight.
  const funnelCurrent = (dpd ? funnelRow : current) || current;

  const domOptions = useMemo(() => (anchor ? dateOptions(anchor) : []), [anchor]);

  const activeBuckets = useMemo(() => {
    if (!dpd) return [];
    const labels = DPD_BUCKETS.map((b) => b.label);
    return buckets.length ? labels.filter((l) => buckets.includes(l)) : labels;
  }, [dpd, buckets]);

  const chartSource = dpd ? (prepared.raw[cfg.grp] || []) : list;
  const chartRows = useMemo(() => [...chartSource].reverse(), [chartSource]);

  const bandGroups = useMemo(() => {
    if (!dpd) return null;
    const forBand = (src, band) =>
      (src || []).filter((r) => r.bucket === band && num(r.accounts_worked) > 0);
    const contextForBand = (band) =>
      CONTEXT_GRPS.flatMap((g) =>
        (prepared.raw[g] || []).filter((r) => r.bucket === band));
    return activeBuckets.map((band) => ({
      label: band,
      rows: [...forBand(prepared.raw[cfg.grp], band), ...contextForBand(band)],
    }));
  }, [dpd, activeBuckets, prepared, cfg.grp]);

  const contextRows = useMemo(
    () => CONTEXT_GRPS.flatMap((g) => prepared[g] || []),
    [prepared]
  );

  // Grouped horizontal bars on the DPD tab, areas on MTD.
  const Chart = dpd ? BandBarChart : ScoreChart;

  if (loading) {
    return (
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
        <CircularProgress size={20} /> Loading scorecard…
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{
        display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS}
                   value={gran} onChange={setGran} />
        <PillGroup label="Amount" options={MONEY_OPTIONS}
                   value={money} onChange={setMoney} />
        {dpd && (
          <MultiPillGroup
            label="Days past due"
            allLabel={DPD_ALL}
            options={DPD_BUCKETS}
            selected={buckets}
            onChange={setBuckets}
          />
        )}
        {gran === "Day" && (
          <PillGroup
            label="Weekday"
            options={DOW_DISPLAY}
            value={dow == null ? null : DOW_LABELS[dow]}
            onChange={(label) => setDow(DOW_LABELS.indexOf(label))}
          />
        )}
        {gran === "Date" && (
          <SelectGroup
            label="Date"
            options={domOptions}
            value={dom}
            onChange={setDom}
          />
        )}
        <Box sx={{ ml: "auto", fontSize: 11, color: "text.secondary" }}>
          Periods resolved in {zoneAbbr(timeZone)}, each ending yesterday
        </Box>
      </Box>

      <SectionTitle>{cfg.title}{dpd ? " by days past due" : ""}</SectionTitle>

      {/* The money chart and the funnel share the top row; the rate chart runs
          full width beneath. The funnel is the CURRENT period only.

          The money chart is PTP $ with Kept $ and Same Day $ STACKED INSIDE IT,
          each a darker green: the bar's height is what was promised, the middle
          band is what was ultimately kept (same day + approved), and the dark
          base is what came in same day. The baseline rule tracks the TOTAL.

          On the DPD tab approved has no dpd to band, so the grouped bars nest
          Same Day only and the funnel's Kept step drops out. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 1.5, mb: 1.5 }}>
        <Chart
          id={`${cfg.money.id}_${moneyCfg.field}`}
          title={titleOf(`${money} by ${cfg.money.by}`)}
          rows={chartRows}
          buckets={activeBuckets}
          field={moneyCfg.field} label={money} color={moneyCfg.color} unit="$"
          parts={moneyCfg.parts}
          partField={moneyCfg.partField} partLabel={moneyCfg.partLabel}
          baseline={activeMean(list, moneyCfg.field)}
        />
        <ChartCard
          title={
            funnelCurrent
              ? `Conversion funnel — ${cfg.funnelName(funnelCurrent)}${funnelCurrent.date_range ? ` · ${funnelCurrent.date_range}` : ""}`
              : `Conversion funnel — ${cfg.funnelFallback}`
          }
          height="auto"
        >
          <Funnel row={funnelCurrent} />
        </ChartCard>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Chart
          id={cfg.rate.id} title={titleOf(cfg.rate.title)} rows={chartRows}
          buckets={activeBuckets}
          field={cfg.rate.field} label={cfg.rate.label} color={cfg.rate.color} unit="%"
          baseline={cfg.rate.baseline(list)}
        />
      </Box>

      {dpd
        ? <ScoreTable groups={bandGroups} />
        : <ScoreTable rows={[...list, ...contextRows]} />}

      <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1.5, lineHeight: 1.5 }}>
        Every period is day-matched: months run from the 1st to the same day
        number, weeks from Monday to the same weekday, the date rows are one
        day number across twelve months — the 9th against the last twelve 9ths
        — and the day rows are twelve occurrences of one weekday a week apart,
        because Monday volume and Saturday volume are different businesses. A
        partial period is never compared against a whole one. All periods end
        yesterday — a part-day would read low against the full days beside it,
        and Prior day at the foot of the table is that same yesterday, whole.
        A date later in the month than yesterday is not offered, because it has
        not happened yet. The dotted line is the pooled rate across the periods
        shown, not the average of their percentages. The Amount pill switches
        the money chart between Same Day $ — what arrived immediately — Kept $,
        which adds the promise payments that later cleared, and PTP $, what was
        promised. These are the same figures the scheduled MTD report carries.
        {dpd && " Each account takes its LATEST days-past-due within a period, so it falls in one band and is counted once. The charts break each period into bands; the table and the funnel add the bands back up. Accounts with no known days-past-due match no band and are not shown. Kept is not split by band yet — approved payments carry no days-past-due — so the banded charts and table show Same Day per band. The funnel adds the bands back up and uses the period's true Kept (same day + approved), so it matches the MTD funnel exactly."}
      </Typography>
    </Box>
  );
}